import { ConnectionQualityLevels } from "@minervaproject/avsubsystem-core";
import { isArray, round, uniq } from "lodash-es";

import { RootState } from "..";
import { PubsubVirtualUserState } from "../../shared/types/av-state";
import { MonitoringEventTypeType, UserStateStatsType } from "../../shared/types/monitoring";

import { createAppSelector } from "./base";

export const selectMonitoringEventCountBySectionIdAndUserIdAndType = createAppSelector(
  [
    (state: RootState) => state.monitoring.stats,
    (state: RootState, sectionId: string | null) => sectionId,
    (state: RootState, sectionId: string | null, userId: string) => userId,
    (
      state: RootState,
      sectionId: string | null,
      userId: string,
      eventType: MonitoringEventTypeType,
    ) => eventType,
  ],
  (stats, sectionId, userId, eventType) => {
    if (!sectionId || !stats[sectionId] || !stats[sectionId][eventType]) {
      return 0;
    }
    const data = stats[sectionId][eventType];
    if (!isArray(data.userIds)) {
      return 0;
    }
    return data.userIds.filter((id) => id === userId).length;
  },
);

export const selectMonitoringEventCountBySectionIdAndTargetClassUserIdAndType = createAppSelector(
  [
    (state: RootState) => state.monitoring.stats,
    (state: RootState, sectionId: string | null) => sectionId,
    (state: RootState, sectionId: string | null, targetClassUserId: string | null) =>
      targetClassUserId,
    (
      state: RootState,
      sectionId: string | null,
      targetClassUserId: string | null,
      eventType: MonitoringEventTypeType,
    ) => eventType,
  ],
  (stats, sectionId, targetClassUserId, eventType) => {
    if (!sectionId || !targetClassUserId || !stats[sectionId] || !stats[sectionId][eventType]) {
      return 0;
    }
    const data = stats[sectionId][eventType];
    if (!isArray(data.targetClassUserIds)) {
      return 0;
    }
    return data.targetClassUserIds.filter((id) => id === targetClassUserId).length;
  },
);

export const selectUserCountWithMonitoringEventsBySectionId = createAppSelector(
  [
    (state: RootState) => state.monitoring.stats,
    (state: RootState, sectionId: string | undefined | null) => sectionId,
  ],
  (stats, sectionId) => {
    if (!sectionId) {
      return 0;
    }
    const userIds = uniq(Object.values(stats[sectionId] || {}).flatMap((x) => x.userIds));
    return userIds.length;
  },
);

const defaultUserStateStats: UserStateStatsType = {
  affectedUsers: [],
  percentAffectedUsers: null,
  formattedPercentage: "-",
};

const computeUserStateStats = (
  userData: Record<string, PubsubVirtualUserState>,
  nParticipants: number,
  filterFn: (userState: PubsubVirtualUserState) => boolean,
): UserStateStatsType => {
  if (!userData || !nParticipants) {
    return defaultUserStateStats;
  }
  const affectedUsers = Object.entries(userData)
    .filter(([, userState]) => filterFn(userState))
    .map(([classUserId]) => classUserId);
  const percentAffectedUsers = (affectedUsers.length / nParticipants) * 100;
  return {
    affectedUsers: uniq(affectedUsers),
    percentAffectedUsers,
    formattedPercentage: `${round(percentAffectedUsers, 2)}%`,
  };
};

export const selectPubsubConnectionStatsBySectionId = createAppSelector(
  [
    (state: RootState) => state.pubsub_virtual_class_state.users,
    (state: RootState, sectionId: string | null) =>
      selectUserCountWithMonitoringEventsBySectionId(state, sectionId),
  ],
  (userData, nParticipants) =>
    computeUserStateStats(
      userData,
      nParticipants,
      (u) => u.pubsubWebsocketStatus === "disconnected",
    ),
);
export const selectMicErrorStatsBySectionId = createAppSelector(
  [
    (state: RootState) => state.pubsub_virtual_class_state.users,
    (state: RootState, sectionId: string | null) =>
      selectUserCountWithMonitoringEventsBySectionId(state, sectionId),
  ],
  (userData, nParticipants) => computeUserStateStats(userData, nParticipants, (u) => !!u.micError),
);

export const selectCameraErrorStatsBySectionId = createAppSelector(
  [
    (state: RootState) => state.pubsub_virtual_class_state.users,
    (state: RootState, sectionId: string | null) =>
      selectUserCountWithMonitoringEventsBySectionId(state, sectionId),
  ],
  (userData, nParticipants) =>
    computeUserStateStats(userData, nParticipants, (u) => !!u.cameraError),
);

export const selectHighPingStatsBySectionId = createAppSelector(
  [
    (state: RootState) => state.pubsub_virtual_class_state.users,
    (state: RootState, sectionId: string | null) =>
      selectUserCountWithMonitoringEventsBySectionId(state, sectionId),
  ],
  (userData, nParticipants) =>
    computeUserStateStats(userData, nParticipants, (u) => !!u.pubsubPingRtt?.isHigh),
);

export const selectPoorConnectionStatsBySectionId = createAppSelector(
  [
    (state: RootState) => state.pubsub_virtual_class_state.users,
    (state: RootState, sectionId: string | null) =>
      selectUserCountWithMonitoringEventsBySectionId(state, sectionId),
  ],
  (userData, nParticipants) =>
    computeUserStateStats(
      userData,
      nParticipants,
      (u) => u.connectionQuality === ConnectionQualityLevels.POOR,
    ),
);

export const selectMonitoringStats = createAppSelector(
  [(state: RootState) => state.monitoring.stats],
  (stats) => stats,
);
